<template>
  <div class="app-header d-flex jcb aic h-100">
    <div
        v-if="!isDesktop"
        class="toggle-menu"
        @click="$emit('toggle-menu')"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#467BFF" stroke-width="2">
        <line x1="3" y1="6" x2="21" y2="6"/>
        <line x1="3" y1="12" x2="21" y2="12"/>
        <line x1="3" y1="18" x2="21" y2="18"/>
      </svg>
    </div>

    <div class="page-name geomanist-font">{{ $t(`navigation.${pageName}`) }}</div>

    <div class="right-header-corner d-flex aic">
      <!-- Vue Admin toggle — visible uniquement pour les superadmin -->
      <div v-if="mainRoleName === 'superadmin'" class="admin-view-toggle d-flex aic">
        <div v-if="isViewingAsAdmin" class="admin-view-badge d-flex aic">
          <span class="admin-view-label">{{ currentOrgName || $t('label.admin_view') }}</span>
        </div>
        <a-tooltip :title="isViewingAsAdmin ? $t('btn.exit_admin_view') : $t('btn.enter_admin_view')">
          <a-switch
              :checked="isViewingAsAdmin"
              size="small"
              @change="toggleAdminView"
          />
        </a-tooltip>
      </div>

      <header-org-switcher
          v-if="showOrgSwitcher"
          :value="currentOrganizationId"
          :organizations="organizationsList"
          @change="onOrganizationChange"
      />
      <notifications-popover/>
      <div v-if="userFullName && isDesktop" class="user-name fs-lg">{{ `${$t('label.hello')}, ${userFullName}` }}</div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import NotificationsPopover from './NotificationsPopover';
import HeaderOrgSwitcher from './HeaderOrgSwitcher.vue';

export default {
  components: {
    NotificationsPopover,
    HeaderOrgSwitcher
  },
  props: {
    isDesktop: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapGetters({
      profile: 'me',
      currentOrganizationId: 'getCurrentOrganizationId',
      isMultiOrg: 'isMultiOrg',
      mainRoleName: 'getMainRoleName',
      isViewingAsAdmin: 'isViewingAsAdmin',
      organizationsList: 'organizationsStore/DROPDOWN_LIST',
    }),
    showOrgSwitcher() {
      return this.mainRoleName === 'superadmin' || this.isMultiOrg;
    },
    pageName() {
      return this.$route.name.toLowerCase();
    },
    userFullName() {
      return this.profile ? `${this.profile.first_name} ${this.profile.last_name}` : null;
    },
    currentOrgName() {
      if (!this.currentOrganizationId || !this.organizationsList?.length) return null;
      return this.organizationsList.find(o => o.id === this.currentOrganizationId)?.name || null;
    }
  },
  created() {
    if (this.showOrgSwitcher) {
      this.$store.dispatch('organizationsStore/GET_DROPDOWN_LIST');
    }
  },
  watch: {
    profile: {
      immediate: true,
      handler(data) {
        if (data) {
          this.$echo.private(`user.${this.profile.id}`)
              .listen('.notifications', response => {
                const notification = response.notification;
                this.$store.commit('socketsStore/SET_NOTIFICATION_RECEIVED', true);
                this.$store.commit('notificationsStore/ADD_NOTIFICATION', notification);
                this.openNotification(notification);
              });
        }
      },
      deep: true
    }
  },
  methods: {
    toggleAdminView(value) {
      this.$store.commit('SET_VIEW_AS_ADMIN', value);
      // Si on quitte la vue admin et qu'on est sur une route admin-only, redirect dashboard
      if (!value) {
        const currentMeta = this.$route.meta;
        if (currentMeta && currentMeta.superadmin === false) {
          this.$router.push({name: 'Dashboard'});
        }
      } else {
        // En entrant en vue admin, si on est sur une route superadmin-only, redirect dashboard
        const currentMeta = this.$route.meta;
        if (currentMeta && currentMeta.admin === false) {
          this.$router.push({name: 'Dashboard'});
        }
      }
    },
    onOrganizationChange(organizationId) {
      this.$store.dispatch('SwitchOrganization', organizationId).then(() => {
        this.$router.go(0);
      }).catch(error => {
        this.$notification.error({
          message: 'Error',
          description: error.response?.data?.error?.message || 'Failed to switch organization',
        });
      });
    },
    openNotification(notification) {
      const key = `open${Date.now()}`;
      const config = {
        message: notification.event_level_label,
        description: notification.message,
        duration: 10,
        btn: h => {
          return h('a-button',
              {
                props: {type: ' app-button primary sm', size: 'small'},
                on: {
                  click: () => {
                    this.$store.dispatch('notificationsStore/MARK_AS_READ', notification.id);
                    this.$notification.close(key);
                  },
                },
              },
              this.$t('btn.mark_as_read'),
          );
        },
        key,
        onClose: close,
      };

      if (notification.event_level === 100) {
        this.$notification.warning(config);
      } else {
        this.$notification.info(config);
      }
    }
  },
  beforeDestroy() {
    this.$echo.leave(`user.${this.profile.id}`);
  }
}
</script>

<style lang="scss" scoped>
.app-header {
  .page-name {
    color: #132540;
    font-size: 21px;
    font-weight: 600;
    line-height: 21px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }

  .toggle-menu {
    margin-right: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    flex-shrink: 0;

    &:hover {
      opacity: 0.7;
    }
  }

  .user-name {
    padding-left: 20px;
    color: #6886A6;
    line-height: 21px;
    white-space: nowrap;
  }

  .right-header-corner {
    gap: 8px;
    flex-shrink: 0;

    .notification-icon {
      height: 24px;
      line-height: 24px;

      &.unread {
        &::after {
          position: absolute;
          height: 4px;
          width: 4px;
          content: '';
          background: #FF655B;
          border-radius: 50%;
        }
      }
    }
  }

  .admin-view-toggle {
    gap: 8px;
    margin-right: 8px;
    padding: 6px 12px;
    border-radius: 6px;
    background: rgba(70, 123, 255, 0.06);
    border: 1px solid rgba(70, 123, 255, 0.2);
    flex-shrink: 0;
  }

  .admin-view-badge {
    gap: 6px;
  }

  .admin-view-label {
    font-size: 12px;
    font-weight: 600;
    color: #467BFF;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}
</style>

<style lang="scss" scoped>
@media screen and (max-width: 768px) {
  .app-header {
    flex-wrap: wrap;
    align-content: flex-start;

    /* Ligne 1 : hamburger + right-corner */
    .toggle-menu {
      order: 1;
      flex-shrink: 0;
    }

    .right-header-corner {
      order: 2;
      margin-left: auto;
    }

    /* Ligne 2 : titre pleine largeur */
    .page-name {
      order: 3;
      width: 100%;
      max-width: 100%;
      font-size: 15px;
      padding-bottom: 8px;
      overflow: visible;
      white-space: normal;
    }

    .admin-view-toggle {
      padding: 4px 8px;
      margin-right: 4px;
    }

    .admin-view-label {
      display: none;
    }
  }
}
</style>
